import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaSearch } from 'react-icons/fa';
import ferramentasApi from '../../services/ferramentasApi.js';
import categoriesApi from '../../services/categoriesApi.js';
import spacesApi from '../../services/spacesApi.js';
import { colors } from '../../utils/colors.js';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterSelect = styled.select`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const List = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 20px;
  background: ${colors.gray[50]};
  color: ${colors.gray[600]};
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 12px 20px;
  border-top: 1px solid ${colors.gray[100]};
  color: ${colors.gray[800]};
  white-space: nowrap;
`;

const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => {
    if (props.$status === 'active') return colors.success.light;
    if (props.$status === 'inactive') return colors.warning.light;
    return colors.gray[100];
  }};
  color: ${(props) => {
    if (props.$status === 'active') return colors.success.dark;
    if (props.$status === 'inactive') return colors.warning.dark;
    return colors.gray[600];
  }};
`;

const SystemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
  font-size: 11px;
  font-weight: 700;
  vertical-align: middle;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => (props.$danger ? colors.error.main : colors.gray[500])};
  font-size: 14px;
  padding: 6px;

  &:hover:not(:disabled) {
    color: ${(props) => (props.$danger ? colors.error.dark : colors.primary[600])};
  }

  &:disabled {
    color: ${colors.gray[300]};
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;

  @media (min-width: 769px) {
    display: flex;
    align-items: stretch;
    justify-content: flex-end;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: stretch;
    justify-content: stretch;
  }
`;

const ModalCard = styled.form`
  background: ${colors.white};
  overflow-y: auto;

  @media (min-width: 769px) {
    width: 480px;
    max-width: 100%;
    height: 100vh;
    padding: 28px;
  }

  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    padding: 20px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.$cols || '1fr'};
  gap: 12px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 70px;
  resize: vertical;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.gray[700]};
  margin-bottom: 20px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const TabsHeader = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${colors.gray[200]};
  margin-bottom: 20px;
`;

const ModalTab = styled.button`
  padding: 8px 4px;
  margin-right: 20px;
  border: none;
  background: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => {
    if (props.disabled) return colors.gray[300];
    return props.$active ? colors.primary[600] : colors.gray[500];
  }};
  border-bottom: 2px solid ${(props) => (props.$active ? colors.primary[600] : 'transparent')};
`;

const HintText = styled.p`
  font-size: 13px;
  color: ${colors.gray[500]};
  margin: 0 0 16px;
`;

const SystemHint = styled(HintText)`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${colors.primary[50]};
  color: ${colors.primary[700]};
`;

const CategoriaGroup = styled.div`
  margin-bottom: 16px;
`;

const CategoriaGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${colors.gray[500]};
  margin-bottom: 8px;
`;

const CategoriaCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.gray[700]};
  padding: 4px 0 4px 8px;
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.gray[400]};
  font-size: 13px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
`;

const SpaceResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid ${colors.gray[100]};
  font-size: 14px;
`;

const SpaceSlugHint = styled.span`
  color: ${colors.gray[400]};
  font-size: 12px;
`;

const EMPTY_FORM = {
  slug: '',
  tipo_ferramenta: 'general',
  visibilidade: 'public',
  status: 'active',
  autenticacao: false,
  nome: '',
  descricao: '',
  tipo_cobranca: 'FREE',
  valor_credito_mensal: 0,
  valor_credito_uso: 0,
  limite_uso_free: 0,
};

function ferramentaToForm(f) {
  return {
    slug: f.slug,
    tipo_ferramenta: f.tipo_ferramenta,
    visibilidade: f.visibilidade,
    status: f.status,
    autenticacao: f.autenticacao,
    nome: f.dados?.nome || '',
    descricao: f.dados?.descricao || '',
    tipo_cobranca: f.dados?.tipo || 'FREE',
    valor_credito_mensal: f.dados?.valor_credito_mensal || 0,
    valor_credito_uso: f.dados?.valor_credito_uso || 0,
    limite_uso_free: f.dados?.limite_uso_free || 0,
  };
}

function isSystemTool(ferramenta) {
  return ferramenta?.is_system === true || ferramenta?.isSystem === true;
}

function getApiErrorMessage(error, fallback) {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.code || fallback;
  }
  return fallback;
}

function formToPayload(form, existingDados = {}, systemTool = false) {
  const payload = {
    tipo_ferramenta: form.tipo_ferramenta,
    visibilidade: form.visibilidade,
    autenticacao: form.autenticacao,
    dados: {
      ...existingDados,
      nome: form.nome,
      descricao: form.descricao,
      tipo: form.tipo_cobranca,
      valor_credito_mensal: Number(form.valor_credito_mensal) || 0,
      valor_credito_uso: Number(form.valor_credito_uso) || 0,
      limite_uso_free: Number(form.limite_uso_free) || 0,
    },
  };

  // Campos de identidade/prioridade de ferramentas de sistema pertencem ao
  // cs_space e nunca devem ser reenviados por uma edição no app_admin.
  if (!systemTool) {
    payload.slug = form.slug;
    payload.status = form.status;
  }

  return payload;
}

function flattenChildCategories(roots) {
  // Só categorias filho (com parent_id) podem ser vinculadas a uma ferramenta como recomendação
  const groups = [];
  for (const root of roots || []) {
    const children = root.children || [];
    if (children.length > 0) {
      groups.push({ parentId: root.id, parentNome: root.nome, children });
    }
  }
  return groups;
}

export default function FerramentaManager() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [modalTab, setModalTab] = useState('dados');
  const [categoriaGroups, setCategoriaGroups] = useState([]);
  const [selectedCategoriaIds, setSelectedCategoriaIds] = useState(new Set());
  const [categoriasLoading, setCategoriasLoading] = useState(false);
  const [linkedSpaces, setLinkedSpaces] = useState([]);
  const [linkedSpacesLoading, setLinkedSpacesLoading] = useState(false);
  const [spaceSearch, setSpaceSearch] = useState('');
  const [spaceSearchDebounced, setSpaceSearchDebounced] = useState('');
  const [spaceSearchResults, setSpaceSearchResults] = useState([]);
  const [spaceSearchLoading, setSpaceSearchLoading] = useState(false);
  const [spaceActionLoadingId, setSpaceActionLoadingId] = useState(null);
  const spaceDebounceRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page: 1, per_page: 100 };
    if (statusFilter) params.status = statusFilter;
    ferramentasApi
      .list(params)
      .then((data) => setItems(data.ferramentas || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce da busca de spaces (aba "Vincular Spaces")
  useEffect(() => {
    if (spaceDebounceRef.current) clearTimeout(spaceDebounceRef.current);
    spaceDebounceRef.current = setTimeout(() => setSpaceSearchDebounced(spaceSearch), 400);
    return () => clearTimeout(spaceDebounceRef.current);
  }, [spaceSearch]);

  useEffect(() => {
    const term = spaceSearchDebounced.trim();
    if (term.length < 2) {
      setSpaceSearchResults([]);
      return;
    }
    setSpaceSearchLoading(true);
    spacesApi
      .list({ search: term, per_page: 10 })
      .then((data) => setSpaceSearchResults(data.items || []))
      .catch(() => setSpaceSearchResults([]))
      .finally(() => setSpaceSearchLoading(false));
  }, [spaceSearchDebounced]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setModalTab('dados');
    setSelectedCategoriaIds(new Set());
    setLinkedSpaces([]);
    setSpaceSearch('');
    setSpaceSearchResults([]);
    setShowModal(true);
  };

  const openEdit = (ferramenta) => {
    setEditing(ferramenta);
    setForm(ferramentaToForm(ferramenta));
    setFormError('');
    setModalTab('dados');
    setSpaceSearch('');
    setSpaceSearchResults([]);
    setShowModal(true);

    setCategoriasLoading(true);
    Promise.all([categoriesApi.hierarchy(), ferramentasApi.getCategorias(ferramenta.id)])
      .then(([hierarchy, vinculos]) => {
        setCategoriaGroups(flattenChildCategories(hierarchy.categories || []));
        setSelectedCategoriaIds(new Set((vinculos.categorias || []).map((c) => c.categoria_id)));
      })
      .catch(() => {
        setCategoriaGroups([]);
        setSelectedCategoriaIds(new Set());
      })
      .finally(() => setCategoriasLoading(false));

    setLinkedSpacesLoading(true);
    ferramentasApi
      .getSpaces(ferramenta.id)
      .then((data) => setLinkedSpaces(data.spaces || []))
      .catch(() => setLinkedSpaces([]))
      .finally(() => setLinkedSpacesLoading(false));
  };

  const handleLinkSpace = async (space) => {
    if (!editing) return;
    setSpaceActionLoadingId(space.id);
    try {
      const created = await ferramentasApi.addSpace(editing.id, space.id);
      setLinkedSpaces((prev) => [...prev, created]);
      setSpaceSearchResults((prev) => prev.filter((s) => s.id !== space.id));
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível vincular o space.');
    } finally {
      setSpaceActionLoadingId(null);
    }
  };

  const handleUnlinkSpace = async (space) => {
    setSpaceActionLoadingId(space.space_id);
    try {
      await ferramentasApi.removeSpace(editing.id, space.space_id);
      setLinkedSpaces((prev) => prev.filter((s) => s.space_id !== space.space_id));
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível remover o vínculo.');
    } finally {
      setSpaceActionLoadingId(null);
    }
  };

  const toggleCategoriaId = (categoriaId) => {
    setSelectedCategoriaIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaId)) {
        next.delete(categoriaId);
      } else {
        next.add(categoriaId);
      }
      return next;
    });
  };

  const handleToggleStatus = async (ferramenta) => {
    if (isSystemTool(ferramenta)) return;
    const novoStatus = ferramenta.status === 'active' ? 'inactive' : 'active';
    try {
      await ferramentasApi.update(ferramenta.id, { status: novoStatus });
      await load();
    } catch (err) {
      window.alert(getApiErrorMessage(err, 'Não foi possível alterar o status.'));
    }
  };

  const handleDelete = async (ferramenta) => {
    if (isSystemTool(ferramenta)) return;
    const nome = ferramenta.dados?.nome || ferramenta.slug;
    if (!window.confirm(`Remover a ferramenta "${nome}"? Ela será arquivada (soft delete).`)) return;
    try {
      await ferramentasApi.remove(ferramenta.id);
      await load();
    } catch (err) {
      window.alert(getApiErrorMessage(err, 'Não foi possível remover a ferramenta.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const payload = formToPayload(form, editing?.dados, isSystemTool(editing));
    try {
      if (editing) {
        await ferramentasApi.update(editing.id, payload);
        await ferramentasApi.setCategorias(editing.id, Array.from(selectedCategoriaIds));
      } else {
        await ferramentasApi.create(payload);
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Não foi possível salvar a ferramenta.'));
    }
  };

  return (
    <div>
      <Header>
        <Title>Ferramentas</Title>
        <HeaderActions>
          <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="active">Ativas</option>
            <option value="inactive">Inativas</option>
            <option value="archived">Arquivadas</option>
          </FilterSelect>
          <AddButton type="button" onClick={openCreate}>
            <FaPlus /> Nova ferramenta
          </AddButton>
        </HeaderActions>
      </Header>

      <List>
        {loading && <Empty>Carregando...</Empty>}
        {!loading && (!items || items.length === 0) && <Empty>Nenhuma ferramenta cadastrada ainda.</Empty>}
        {!loading && items && items.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Nome</Th>
                <Th>Slug</Th>
                <Th>Tipo</Th>
                <Th>Visibilidade</Th>
                <Th>Status</Th>
                <Th>Spaces usando</Th>
                <Th>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id}>
                  <Td>
                    {f.dados?.nome || f.slug}
                    {isSystemTool(f) && <SystemBadge>Sistema</SystemBadge>}
                  </Td>
                  <Td>{f.slug}</Td>
                  <Td>{f.tipo_ferramenta}</Td>
                  <Td>{f.visibilidade}</Td>
                  <Td>
                    <Badge $status={f.status}>{f.status}</Badge>
                  </Td>
                  <Td>{f.total_spaces ?? 0}</Td>
                  <Td>
                    <Actions>
                      <IconButton
                        type="button"
                        onClick={() => handleToggleStatus(f)}
                        title={
                          isSystemTool(f)
                            ? 'Ferramentas de sistema não podem ser desativadas'
                            : f.status === 'active'
                              ? 'Inativar'
                              : 'Ativar'
                        }
                        disabled={f.status === 'archived' || isSystemTool(f)}
                      >
                        {f.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                      </IconButton>
                      <IconButton
                        type="button"
                        onClick={() => openEdit(f)}
                        title={
                          isSystemTool(f)
                            ? 'Ferramentas de sistema são configuradas na gestão do Space'
                            : 'Editar'
                        }
                        disabled={isSystemTool(f)}
                      >
                        <FaEdit />
                      </IconButton>
                      <IconButton
                        type="button"
                        $danger
                        onClick={() => handleDelete(f)}
                        title={
                          isSystemTool(f)
                            ? 'Ferramentas de sistema não podem ser removidas'
                            : 'Remover'
                        }
                        disabled={isSystemTool(f)}
                      >
                        <FaTrash />
                      </IconButton>
                    </Actions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </List>

      {showModal && createPortal(
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <ModalTitle>{editing ? 'Editar ferramenta' : 'Nova ferramenta'}</ModalTitle>

            {formError && <ErrorBox>{formError}</ErrorBox>}
            {isSystemTool(editing) && (
              <SystemHint>
                Ferramenta de sistema: slug, prioridade e status são controlados pelo cs_space.
              </SystemHint>
            )}

            <TabsHeader>
              <ModalTab type="button" $active={modalTab === 'dados'} onClick={() => setModalTab('dados')}>
                Dados
              </ModalTab>
              <ModalTab
                type="button"
                $active={modalTab === 'categorias'}
                disabled={!editing}
                title={!editing ? 'Salve a ferramenta antes de vincular categorias' : undefined}
                onClick={() => editing && setModalTab('categorias')}
              >
                Categorias
              </ModalTab>
              <ModalTab
                type="button"
                $active={modalTab === 'spaces'}
                disabled={!editing}
                title={!editing ? 'Salve a ferramenta antes de vincular spaces' : undefined}
                onClick={() => editing && setModalTab('spaces')}
              >
                Vincular Spaces
              </ModalTab>
            </TabsHeader>

            {modalTab === 'spaces' ? (
              <>
                <HintText>
                  Vincule esta ferramenta diretamente a spaces específicos (uso típico: ferramentas VIP, que não
                  aparecem na loja pública). O vínculo é criado inativo — o dono do space precisa ativá-lo depois.
                </HintText>

                <SearchBox>
                  <SearchIcon />
                  <SearchInput
                    placeholder="Buscar space por nome ou slug (mín. 2 caracteres)..."
                    value={spaceSearch}
                    onChange={(e) => setSpaceSearch(e.target.value)}
                  />
                </SearchBox>
                {spaceSearchLoading && <Empty>Buscando...</Empty>}
                {!spaceSearchLoading &&
                  spaceSearchResults.map((s) => (
                    <SpaceResultRow key={s.id}>
                      <span>
                        {s.nome} <SpaceSlugHint>({s.slug})</SpaceSlugHint>
                      </span>
                      <IconButton
                        type="button"
                        onClick={() => handleLinkSpace(s)}
                        disabled={spaceActionLoadingId === s.id}
                        title="Vincular"
                      >
                        {spaceActionLoadingId === s.id ? '...' : <FaPlus />}
                      </IconButton>
                    </SpaceResultRow>
                  ))}

                <CategoriaGroupTitle style={{ marginTop: 20 }}>Spaces vinculados</CategoriaGroupTitle>
                {linkedSpacesLoading && <Empty>Carregando...</Empty>}
                {!linkedSpacesLoading && linkedSpaces.length === 0 && (
                  <Empty>Nenhum space vinculado ainda.</Empty>
                )}
                {!linkedSpacesLoading &&
                  linkedSpaces.map((s) => (
                    <SpaceResultRow key={s.space_id}>
                      <span>
                        {s.space_nome} <SpaceSlugHint>({s.space_slug})</SpaceSlugHint>
                      </span>
                      <IconButton
                        type="button"
                        $danger
                        onClick={() => handleUnlinkSpace(s)}
                        disabled={spaceActionLoadingId === s.space_id}
                        title="Remover vínculo"
                      >
                        {spaceActionLoadingId === s.space_id ? '...' : <FaTrash />}
                      </IconButton>
                    </SpaceResultRow>
                  ))}
              </>
            ) : modalTab === 'categorias' ? (
              <>
                <HintText>
                  Marque as categorias filho para as quais esta ferramenta deve aparecer como recomendada na loja
                  de ferramentas do space.
                </HintText>
                {categoriasLoading && <Empty>Carregando categorias...</Empty>}
                {!categoriasLoading && categoriaGroups.length === 0 && (
                  <Empty>Nenhuma categoria com subcategorias cadastrada ainda.</Empty>
                )}
                {!categoriasLoading &&
                  categoriaGroups.map((group) => (
                    <CategoriaGroup key={group.parentId}>
                      <CategoriaGroupTitle>{group.parentNome}</CategoriaGroupTitle>
                      {group.children.map((child) => (
                        <CategoriaCheckboxRow key={child.id}>
                          <input
                            type="checkbox"
                            checked={selectedCategoriaIds.has(child.id)}
                            onChange={() => toggleCategoriaId(child.id)}
                          />
                          {child.nome}
                        </CategoriaCheckboxRow>
                      ))}
                    </CategoriaGroup>
                  ))}
              </>
            ) : (
              <>
            <Label htmlFor="nome">Nome</Label>
            <Input
              id="nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              required
              autoFocus
            />

            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              required
              disabled={!!editing || isSystemTool(editing)}
            />

            <Label htmlFor="descricao">Descrição</Label>
            <TextArea
              id="descricao"
              value={form.descricao}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            />

            <Row $cols="1fr 1fr">
              <div>
                <Label htmlFor="tipo_ferramenta">Tipo</Label>
                <Select
                  id="tipo_ferramenta"
                  value={form.tipo_ferramenta}
                  onChange={(e) => setForm((f) => ({ ...f, tipo_ferramenta: e.target.value }))}
                >
                  <option value="general">Geral</option>
                  <option value="tickets">Tickets</option>
                  <option value="rifa">Rifa</option>
                  <option value="forms">Formulários</option>
                  <option value="programacao">Programação</option>
                  <option value="assembleia">Assembleia</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="visibilidade">Visibilidade</Label>
                <Select
                  id="visibilidade"
                  value={form.visibilidade}
                  onChange={(e) => setForm((f) => ({ ...f, visibilidade: e.target.value }))}
                >
                  <option value="public">Pública</option>
                  <option value="private">Privada</option>
                  <option value="restricted">Restrita</option>
                  <option value="vip">VIP</option>
                </Select>
              </div>
            </Row>

            <Row $cols="1fr 1fr">
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  id="status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  disabled={isSystemTool(editing)}
                >
                  <option value="active">Ativa</option>
                  <option value="inactive">Inativa</option>
                  <option value="archived">Arquivada</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="tipo_cobranca">Cobrança</Label>
                <Select
                  id="tipo_cobranca"
                  value={form.tipo_cobranca}
                  onChange={(e) => setForm((f) => ({ ...f, tipo_cobranca: e.target.value }))}
                >
                  <option value="FREE">Gratuita</option>
                  <option value="PREMIUM">Premium</option>
                </Select>
              </div>
            </Row>

            <Row $cols="1fr 1fr 1fr">
              <div>
                <Label htmlFor="valor_credito_mensal">Créd. mensal</Label>
                <Input
                  id="valor_credito_mensal"
                  type="number"
                  step="0.01"
                  value={form.valor_credito_mensal}
                  onChange={(e) => setForm((f) => ({ ...f, valor_credito_mensal: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="valor_credito_uso">Créd. por uso</Label>
                <Input
                  id="valor_credito_uso"
                  type="number"
                  step="0.01"
                  value={form.valor_credito_uso}
                  onChange={(e) => setForm((f) => ({ ...f, valor_credito_uso: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="limite_uso_free">Limite free</Label>
                <Input
                  id="limite_uso_free"
                  type="number"
                  value={form.limite_uso_free}
                  onChange={(e) => setForm((f) => ({ ...f, limite_uso_free: e.target.value }))}
                />
              </div>
            </Row>

            <CheckboxRow>
              <input
                type="checkbox"
                checked={form.autenticacao}
                onChange={(e) => setForm((f) => ({ ...f, autenticacao: e.target.checked }))}
              />
              Exige autenticação do usuário para uso
            </CheckboxRow>
              </>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={() => setShowModal(false)}>
                Cancelar
              </CancelButton>
              <SaveButton type="submit">Salvar</SaveButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>,
        document.body
      )}
    </div>
  );
}
